// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Main-thread client for the analysis Web Worker. Owns a single long-lived
// module worker, correlates responses to requests by id, and degrades silently
// to a rejected promise whenever the worker is disabled, unsupported, or fails.
// Callers treat a rejection as "fall back to the synchronous detector pass" —
// analysis is a pure optimization, so flight loading must never break because
// of the worker.

// Master switch. Set to false to force the legacy synchronous pass everywhere
// (the worker is never constructed and runRawAnalysisInWorker always rejects).
export const ANALYSIS_WORKER_ENABLED = true;

let worker = null;
// Latched once a worker fails to construct, errors out, or rejects a structured
// clone. The payload shape is identical on every call, so a clone failure will
// recur — latching avoids paying the cost (and spamming errors) again.
let workerBroken = false;
let nextRequestId = 1;
const pending = new Map();

function rejectAllPending(reason) {
  for (const { reject } of pending.values()) reject(reason);
  pending.clear();
}

function handleMessage(event) {
  const { id, ok, bundle, perfMarks, error } = event.data ?? {};
  const entry = pending.get(id);
  if (!entry) return;
  pending.delete(id);
  if (ok) entry.resolve({ bundle, perfMarks: Array.isArray(perfMarks) ? perfMarks : [] });
  else entry.reject(new Error(error || "analysis-worker-failed"));
}

function handleError(event) {
  workerBroken = true;
  rejectAllPending(new Error(event?.message || "analysis-worker-error"));
  try { worker?.terminate(); } catch { /* ignore */ }
  worker = null;
}

function ensureWorker() {
  if (workerBroken) return null;
  if (worker) return worker;
  if (typeof Worker === "undefined") { workerBroken = true; return null; }
  try {
    worker = new Worker(new URL("../analysis/analysis-worker.js", import.meta.url), { type: "module" });
    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);
    worker.addEventListener("messageerror", handleError);
  } catch {
    workerBroken = true;
    worker = null;
  }
  return worker;
}

// True when a worker call is worth attempting. Callers still handle rejection.
export function analysisWorkerAvailable() {
  return ANALYSIS_WORKER_ENABLED && typeof Worker !== "undefined" && !workerBroken;
}

// Resolves to { bundle, perfMarks }. Rejects (so the caller can fall back to the
// synchronous pass) if the worker is disabled, unavailable, or errors.
export function runRawAnalysisInWorker({ data, options }) {
  return new Promise((resolve, reject) => {
    if (!ANALYSIS_WORKER_ENABLED) { reject(new Error("analysis-worker-disabled")); return; }
    const active = ensureWorker();
    if (!active) { reject(new Error("analysis-worker-unavailable")); return; }
    const id = nextRequestId;
    nextRequestId += 1;
    pending.set(id, { resolve, reject });
    try {
      active.postMessage({ id, data, options });
    } catch (error) {
      pending.delete(id);
      // A synchronous postMessage throw is a structured-clone failure: this
      // payload can't cross the boundary, and neither will the next one. Latch
      // broken and fall back to sync from here on.
      workerBroken = true;
      try { active.terminate(); } catch { /* ignore */ }
      worker = null;
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}
